import { faMicrophone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import parse from 'html-react-parser';
import React, { useCallback, useRef, useState } from 'react';
import { Card, useAccordionButton } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { Element } from 'react-scroll';

import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import endpoints from '../../../constants/configs/endpoints';
import { useAppSelector } from '../../../hooks';
import { useAccountActivityState } from '../../../hooks/useAccountActivityState';
import { M_D_YYYY_H_MM_A, formatDateString } from '../../../utils/dates/dateMethods';
import { configs } from '../utils';
import {
    StyledAccordion,
    StyledAccordionCollapse,
    StyledAccordionHeader,
    StyledAccordionHeaderContainer,
    StyledAccordionHeaderRole,
    StyledAccordionHeaderTimestamp,
    StyledAccordionHeaderTitle,
    StyledAccordionSubHeader,
    StyledCard,
    StyledCardHeader,
    StyledEventContainer,
    StyledEventIdValue,
    StyledEventKey,
    StyledEventValue,
    StyledHeading,
    StyledHr,
    StyledInterationContainer,
    StyledScrollElement,
    StyledUserRole,
} from './AccountActivity.styled';
import AddCallNoteModal from './AddCallNoteModal';
import {
    CATEGORY_TYPES,
    COLON_SEPARATOR,
    CUSTOMER_CALLS_LOGGED_CALL_EVENT,
    CUSTOMER_INTERACTIONS_CHAT_EVENT,
    CUSTOMER_TICKET_COMMENTS_EVENT,
    CUSTOMER_TICKET_TRANSFERRED_EVENT,
    IS_TICKET_COMMENT_DELETED,
    IS_TICKET_COMMENT_PUBLIC,
    MAP_PARAMETERS,
    NOTE_TYPES,
} from './common/accountActivity.constants';
import { CustomerNotesSection } from './CustomerNotesSection';
import { EventComments } from './EventComments';
import InteractionDetails from './InteractionDetails';

interface IAccountActivityEventsProps {
    activeKey: string | null;
    setActiveKey: React.Dispatch<React.SetStateAction<string | null>>;
    fetchComments: (eventId: string) => void;
}

const EXPANDED_ICON = 'chevron-up';
const COLLAPSED_ICON = 'chevron-down';

// Utility function moved outside component to avoid recreation on every render
const isStringOrNumber = (value: unknown): boolean => {
    return typeof value === 'string' || typeof value === 'number';
};

function CustomToggle({
    item,
    event,
    activeKey,
    setActiveKey,
    viewTicketDetailsLink,
    fetchComments,
    onAddCallNoteModalOpen,
    onAddChatNoteModalOpen,
}) {
    const isActive = activeKey === item.id;
    const { isLinkForEventName } = useAccountActivityState();
    const onAccordionBtnClick = useAccordionButton(item.id, () => {
        setActiveKey(isActive ? null : item.id);
    });

    const getEventName = () => {
        const eventName = `${item.type} - ${item.name}`;
        return isLinkForEventName ? <span style={{ cursor: 'pointer', textDecoration: 'underline' }}>{eventName}</span> : <span>{eventName}</span>;
    };

    const isCommentDeleted =
        event.mapParameters?.[MAP_PARAMETERS.TICKET_COMMENT_DELETED]?.toLowerCase() === IS_TICKET_COMMENT_DELETED;

    const itemType = item.type.toLowerCase();
    const itemName = item.name.toLowerCase();

    const TicketDetailsLink = ({ event }: { event: any }) => (
        <span data-testid="ticket-details-link">
            <ConditionalRendering isTrue={!viewTicketDetailsLink}>
                {event.mapParameters?.[MAP_PARAMETERS.TICKET_NUMBER]}
            </ConditionalRendering>
            <ConditionalRendering isTrue={viewTicketDetailsLink}>
                <a
                    href={endpoints.TICKET_MGMT_TICKET_DETAIL.processReplacements({
                        encTicketId: event.Enc_Ticket_Number,
                    })}
                    target="_blank"
                >
                    {event.mapParameters?.[MAP_PARAMETERS.TICKET_NUMBER]}
                </a>
            </ConditionalRendering>
        </span>
    );

    const TicketTransferredEventSubHeader = ({ event }: { event: any }) => (
        <ConditionalRendering isTrue={itemName === CUSTOMER_TICKET_TRANSFERRED_EVENT.toLowerCase()}>
            <span>{COLON_SEPARATOR}</span>
            <ConditionalRendering isTrue={!!event.mapParameters?.[MAP_PARAMETERS.TICKET_TRANSFER_MSG]}>
                <span className="ticket-subHeader" data-testid="ticket-transferred-event-subHeader">
                    {event.mapParameters?.[MAP_PARAMETERS.TICKET_TRANSFER_MSG]}
                </span>
            </ConditionalRendering>
        </ConditionalRendering>
    );

    const TicketCommentsEventSubHeder = ({ event }: { event: any }) => (
        <ConditionalRendering isTrue={itemName === CUSTOMER_TICKET_COMMENTS_EVENT.toLowerCase()}>
            <span>{COLON_SEPARATOR}</span>
            <ConditionalRendering isTrue={!!event.mapParameters?.[MAP_PARAMETERS.TICKET_SUBJECT]}>
                <span className="ticket-subHeader" data-testid="ticket-subject">
                    {event.mapParameters?.[MAP_PARAMETERS.TICKET_SUBJECT]}
                </span>
            </ConditionalRendering>
            <ConditionalRendering isTrue={isCommentDeleted}>
                <i className="ticket-subHeader" data-testid="comment-deleted">
                    <FormattedMessage id="member.account.activity.ticket.comment.deleted" />
                </i>
            </ConditionalRendering>
            <ConditionalRendering isTrue={!isCommentDeleted}>
                <ConditionalRendering
                    isTrue={event.mapParameters?.[MAP_PARAMETERS.TICKET_PUBLIC_COMMENT] === IS_TICKET_COMMENT_PUBLIC}
                >
                    <i className="ticket-subHeader" data-testid="public-comment">
                        <FormattedMessage id="member.account.activity.ticket.comment.public" />
                    </i>
                </ConditionalRendering>
            </ConditionalRendering>
        </ConditionalRendering>
    );

    const TicketEventsSubHeader = ({ event }: { event: any }) => (
        <ConditionalRendering
            isTrue={
                itemName !== CUSTOMER_TICKET_COMMENTS_EVENT.toLowerCase() &&
                itemName !== CUSTOMER_TICKET_TRANSFERRED_EVENT.toLowerCase()
            }
        >
            <ConditionalRendering isTrue={!event.mapParameters?.[MAP_PARAMETERS.TICKET_SUBJECT]}>
                <span className="ticket-subHeader">
                    <FormattedMessage id="member.account.activity.event.of.workgroup" />
                </span>
                <span className="ticket-subHeader" data-testid="ticket-workgroup">
                    {event.mapParameters?.[MAP_PARAMETERS.TICKET_WORKGROUP]}
                </span>
            </ConditionalRendering>
            <ConditionalRendering isTrue={!!event.mapParameters?.[MAP_PARAMETERS.TICKET_SUBJECT]}>
                <span
                    className="ticket-subHeader"
                    data-testid="ticket-subject"
                >{`: ${event.mapParameters?.[MAP_PARAMETERS.TICKET_SUBJECT]}`}</span>
            </ConditionalRendering>
        </ConditionalRendering>
    );

    return (
        <StyledAccordionHeaderContainer onClick={onAccordionBtnClick} data-testid={`event-custom-header-${item.id}`}>
            <StyledAccordionHeader>
                <StyledAccordionHeaderTitle data-testid="event-name">{getEventName()}</StyledAccordionHeaderTitle>
                <StyledAccordionHeaderTimestamp data-testid="event-timestamp">{`${formatDateString(
                    item.creationTimeStamp,
                    M_D_YYYY_H_MM_A,
                )}`}</StyledAccordionHeaderTimestamp>
                <StyledAccordionHeaderRole data-testid="event-role">
                    {`${item.createdByUserName} - (`}
                    <StyledUserRole>{`${item.createdByUserRole?.toLowerCase()})`}</StyledUserRole>
                </StyledAccordionHeaderRole>
                <FontAwesomeIcon icon={isActive ? EXPANDED_ICON : COLLAPSED_ICON} />
            </StyledAccordionHeader>
            <ConditionalRendering isTrue={itemType === CATEGORY_TYPES.TICKETS.toLowerCase()}>
                <StyledHr />
                <StyledAccordionSubHeader data-testid="event-subHeader">
                    <TicketDetailsLink event={event} />
                    <TicketTransferredEventSubHeader event={event} />
                    <TicketCommentsEventSubHeder event={event} />
                    <TicketEventsSubHeader event={event} />
                </StyledAccordionSubHeader>
            </ConditionalRendering>
            <ConditionalRendering
                isTrue={
                    itemType === CATEGORY_TYPES.CUSTOMER_CALLS.toLowerCase() &&
                    itemName === CUSTOMER_CALLS_LOGGED_CALL_EVENT.toLowerCase()
                }
            >
                <StyledHr />
                <CustomerNotesSection
                    item={item}
                    fetchComments={fetchComments}
                    activeKey={activeKey}
                    title="member.account.activity.note.call.title"
                    buttonLabel="member.account.activity.note.call.button"
                    buttonId="add_call_note_btn"
                    onButtonClick={onAddCallNoteModalOpen}
                />
            </ConditionalRendering>
            <ConditionalRendering
                isTrue={
                    itemType === CATEGORY_TYPES.CUSTOMER_INTERACTIONS.toLowerCase() &&
                    itemName === CUSTOMER_INTERACTIONS_CHAT_EVENT.toLowerCase()
                }
            >
                <StyledHr />
                <CustomerNotesSection
                    item={item}
                    fetchComments={fetchComments}
                    activeKey={activeKey}
                    title="member.account.activity.note.chat.title"
                    buttonLabel="member.account.activity.note.chat.button"
                    buttonId="add_chat_note_btn"
                    onButtonClick={onAddChatNoteModalOpen}
                />
            </ConditionalRendering>
        </StyledAccordionHeaderContainer>
    );
}

export const AccountActivityEvents: React.FC<IAccountActivityEventsProps> = ({
    activeKey,
    setActiveKey,
    fetchComments,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef(null);
    const intlApi = useIntl();
    const { initialData, currentFilteredData } = useAppSelector(({ accountActivity }) => accountActivity);

    const {
        viewTicketDetailsLink,
        memberHistoryPermissionMap: { memberAbtShowcomments },
    } = initialData;

    const [noteModalTarget, setNoteModalTarget] = useState<{ type: NOTE_TYPES; eventId: string } | null>(null);

    const openCallNoteModal = useCallback(
        (eventId: string) => setNoteModalTarget({ type: NOTE_TYPES.CALL, eventId }),
        [],
    );
    const openChatNoteModal = useCallback(
        (eventId: string) => setNoteModalTarget({ type: NOTE_TYPES.CHAT, eventId }),
        [],
    );
    const closeNoteModal = useCallback(() => setNoteModalTarget(null), []);

    function navigateToRecordingUrl(e, recordingUrl: string) {
        e.preventDefault();
        window.open(
            recordingUrl,
            'mediaWindow',
            'status=no, toolbar=no, location=no, menubar=no, resizable=yes, scrollbars=no, noopener, noreferrer',
        );
    }

    function formatFieldName(key: string): string {
        if (!key) return '';

        // Capitalize the first letter and split the string into words at camelCase transitions
        return key
            .replace(/^./, match => match.toUpperCase()) // Capitalize the first character
            .replace(/([a-z])([A-Z])/g, '$1 $2'); // Add space before capital letters not preceded by another capital
    }

    const parseString = message => {
        return typeof message === 'string' ? parse(message) : message;
    };

    const TicketComment = ({ item, event }) => {
        const isCommentDeleted =
            event.mapParameters?.[MAP_PARAMETERS.TICKET_COMMENT_DELETED]?.toLowerCase() === IS_TICKET_COMMENT_DELETED;

        return (
            <ConditionalRendering isTrue={item.name === CUSTOMER_TICKET_COMMENTS_EVENT}>
                <ConditionalRendering
                    isTrue={event.mapParameters?.[MAP_PARAMETERS.TICKET_COMMENT] && !isCommentDeleted}
                >
                    {parseString(event.mapParameters?.[MAP_PARAMETERS.TICKET_COMMENT])}
                </ConditionalRendering>
            </ConditionalRendering>
        );
    };

    return (
        <>
            <StyledAccordion className="styled-accordion" ref={containerRef} flush activeKey={activeKey}>
                <StyledScrollElement name="containerElement" id="containerElement">
                    {currentFilteredData.map(item => {
                        const event = JSON.parse(item.eventJson || '');
                        const recordingUrl = `${configs.routes.MEDIA_ACCESS}?contactId=${event?.interationId}`;
                        return (
                            <StyledCard
                                id={`card-${item.id}`}
                                data-testid={`card-${item.id}`}
                                key={item.id}
                                isActive={activeKey === item.id}
                            >
                                <Element name={item.id} id={item.id}>
                                    <StyledCardHeader
                                        id={item.id}
                                        ref={headerRef}
                                        isActive={activeKey === item.id}
                                        data-testid={`event-card-header-${item.id}`}
                                    >
                                        <CustomToggle
                                            event={event}
                                            item={item}
                                            activeKey={activeKey}
                                            setActiveKey={setActiveKey}
                                            viewTicketDetailsLink={viewTicketDetailsLink}
                                            fetchComments={fetchComments}
                                            onAddCallNoteModalOpen={openCallNoteModal}
                                            onAddChatNoteModalOpen={openChatNoteModal}
                                        />
                                    </StyledCardHeader>
                                    <StyledAccordionCollapse eventKey={item.id} isActive={activeKey === item.id}>
                                        <Card.Body>
                                            <div data-testid={`${item.id}-event-body`}>
                                                <TicketComment event={event} item={item} />
                                                <ConditionalRendering
                                                    isTrue={item.name !== CUSTOMER_TICKET_COMMENTS_EVENT}
                                                >
                                                    <ConditionalRendering isTrue={!!event.interationId}>
                                                        <StyledInterationContainer>
                                                            <ConditionalRendering isTrue={!!event.recordingUrl}>
                                                                <button
                                                                    type="button"
                                                                    title={intlApi.formatMessage({
                                                                        id: 'member.account.activity.recordingUrl.title',
                                                                    })}
                                                                    onClick={e => {
                                                                        navigateToRecordingUrl(e, recordingUrl);
                                                                    }}
                                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                                                >
                                                                    <FontAwesomeIcon icon={faMicrophone} />
                                                                </button>
                                                            </ConditionalRendering>

                                                            <StyledHeading data-testid="interaction-id-key">
                                                                <FormattedMessage id="member.account.activity.interaction.id" />
                                                                {':'}
                                                            </StyledHeading>
                                                            <StyledEventIdValue data-testid="interaction-id-value">
                                                                {event.interationId}
                                                            </StyledEventIdValue>
                                                        </StyledInterationContainer>
                                                    </ConditionalRendering>
                                                    <ConditionalRendering isTrue={!!event.nextSteps}>
                                                        <StyledEventContainer>
                                                            <StyledEventKey data-testid="next-steps-key">
                                                                <FormattedMessage id="member.account.activity.next.steps" />
                                                            </StyledEventKey>
                                                            <StyledEventValue data-testid="next-steps-value">
                                                                {event.nextSteps}
                                                            </StyledEventValue>
                                                        </StyledEventContainer>
                                                    </ConditionalRendering>
                                                    <ConditionalRendering isTrue={!event.interationId}>
                                                        {event.mapParameters &&
                                                            Object.entries(event.mapParameters)
                                                                .filter(
                                                                    ([key]) =>
                                                                        key !== MAP_PARAMETERS.TICKET_TRANSFER_MSG,
                                                                )
                                                                .map(([key, value]) => {
                                                                    // Display value as-is from backend (server time)
                                                                    const displayValue = isStringOrNumber(value) ? String(value) : '';

                                                                    return (
                                                                        <StyledEventContainer key={key}>
                                                                            <StyledEventKey data-testid={`${key}-key`}>
                                                                                {formatFieldName(key)}
                                                                            </StyledEventKey>
                                                                            <StyledEventValue data-testid={`${key}-value`}>
                                                                                {displayValue}
                                                                            </StyledEventValue>
                                                                        </StyledEventContainer>
                                                                    );
                                                                })}
                                                    </ConditionalRendering>
                                                    <ConditionalRendering isTrue={!!event.interationId}>
                                                        <InteractionDetails
                                                            sessionDetails={event?.mapParameters}
                                                            aiCallAnalysis={event?.aiCallAnalysis}
                                                        />
                                                    </ConditionalRendering>
                                                    <ConditionalRendering
                                                        isTrue={memberAbtShowcomments && !event.interationId}
                                                    >
                                                        <EventComments
                                                            item={item}
                                                            fetchComments={id => fetchComments(id)}
                                                        />
                                                    </ConditionalRendering>
                                                </ConditionalRendering>
                                            </div>
                                        </Card.Body>
                                    </StyledAccordionCollapse>
                                </Element>
                            </StyledCard>
                        );
                    })}
                </StyledScrollElement>
            </StyledAccordion>
            <AddCallNoteModal
                id={noteModalTarget?.type === NOTE_TYPES.CALL ? noteModalTarget.eventId : null}
                type={NOTE_TYPES.CALL}
                isOpen={noteModalTarget?.type === NOTE_TYPES.CALL}
                onClose={closeNoteModal}
                refetchComments={
                    noteModalTarget?.type === NOTE_TYPES.CALL
                        ? () => fetchComments(noteModalTarget.eventId)
                        : undefined
                }
            />
            <AddCallNoteModal
                id={noteModalTarget?.type === NOTE_TYPES.CHAT ? noteModalTarget.eventId : null}
                type={NOTE_TYPES.CHAT}
                isOpen={noteModalTarget?.type === NOTE_TYPES.CHAT}
                onClose={closeNoteModal}
                refetchComments={
                    noteModalTarget?.type === NOTE_TYPES.CHAT
                        ? () => fetchComments(noteModalTarget.eventId)
                        : undefined
                }
            />
        </>
    );
};
